import { useState, useEffect, useCallback } from 'react';

interface UsePresenceProps {
  tableId: string;
  subscribe: (fn: (data: any) => void) => () => void;
  isConnected?: boolean;
}

interface UsePresenceReturn {
  onlineUsers: string[];
  isUserOnline: (userId: string) => boolean;
  onlineCount: number;
}

/**
 * Hook para gerenciar presença de usuários em tempo real
 * Escuta eventos de presença via WebSocket e mantém lista de usuários online
 */
export function usePresence({
  tableId,
  subscribe,
  isConnected = false
}: UsePresenceProps): UsePresenceReturn {
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);

  // Função para verificar se um usuário está online. useCallback: sem ele a
  // identidade mudava a cada render e o arePropsEqual dos layouts (que agora
  // compara isUserOnline por referencia) nunca conseguiria distinguir uma
  // mudanca real de presenca. Agora so muda quando onlineUsers muda.
  const isUserOnline = useCallback((userId: string): boolean => {
    return onlineUsers.includes(userId);
  }, [onlineUsers]);

  // Função para processar eventos de presença
    const handlePresenceUpdate = useCallback((message: any) => {
      // Ignorar mensagens que não são de presença
      // Mensagens de presença têm a propriedade online_users
      if (!message?.online_users) {
        return;
      }
      
      if (Array.isArray(message.online_users)) {
        setOnlineUsers(message.online_users);
      }
    }, []);

  // Inscrever no hub do useGameWebSocket (parse único e centralizado) em vez
  // de adicionar listener próprio no socket cru.
  useEffect(() => {
    const unsubscribe = subscribe(handlePresenceUpdate);
    return unsubscribe;
  }, [subscribe, handlePresenceUpdate]);

  // Limpar estado quando desconectado
  useEffect(() => {
    if (!isConnected) {
      setOnlineUsers([]);
    }
  }, [isConnected]);

  return {
    onlineUsers,
    isUserOnline,
    onlineCount: onlineUsers.length
  };
}